// "Pending in portal" — prospects pushed into the client portal but not yet
// converted to active clients. Reads GET /api/data/portal-pending and renders
// status (Invited -> In onboarding -> Signed + paid) with a Remove access
// action. When a prospect signs + pays they're "ready to convert"; staff then
// create the Active engagement in BCC, which flips the household to a client
// and drops it off this list.

import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../utils/apiFetch';

const STATUS_META = {
  signed_paid:   { label: 'Signed + paid',  cls: 'bg-deep-teal/15 text-deep-teal' },
  in_onboarding: { label: 'In onboarding',  cls: 'bg-bullogic-blue/15 text-bullogic-blue' },
  invited:       { label: 'Invited',        cls: 'bg-navy/10 text-navy' },
  not_started:   { label: 'Not started',    cls: 'bg-gray-100 text-gray-600' },
  access_removed:{ label: 'Access removed',  cls: 'bg-gray-100 text-gray-500' },
};

function fmtDate(iso) {
  if (!iso) return '--';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '--';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function PortalPendingTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actioningId, setActioningId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/data/portal-pending');
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `Failed to load (HTTP ${res.status})`);
      setRows(Array.isArray(data.pending) ? data.pending : []);
    } catch (e) {
      setError(e.message || 'Failed to load pending list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleRemoveAccess(row) {
    const who = row.client_name || row.household_id;
    if (!window.confirm(`Remove portal access for "${who}"? Both logins are disabled immediately. The household and its history are kept; you can see it here as "Access removed".`)) {
      return;
    }
    setActioningId(row.household_id);
    try {
      const res = await apiFetch(`/api/data/portal-pending/${encodeURIComponent(row.household_id)}/remove-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Remove access failed (HTTP ${res.status})`);
      await load();
    } catch (e) {
      window.alert(e.message || 'Could not remove access. Please try again.');
    } finally {
      setActioningId(null);
    }
  }

  const total = rows.length;
  const readyCount = rows.filter(r => r.ready_to_convert).length;

  const headClass = 'px-3 py-2 text-left text-[10px] font-bold uppercase tracking-[0.08em] text-white';
  const cellClass = 'px-3 py-2 text-[12px] text-navy align-middle';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-[18px] font-black text-navy">Pending in portal</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[12px] font-bold px-2.5 py-1 rounded-full bg-navy/10 text-navy">{total} pending</span>
          {readyCount > 0 && (
            <span className="text-[12px] font-bold px-2.5 py-1 rounded-full bg-deep-teal/15 text-deep-teal">{readyCount} ready to convert</span>
          )}
          <button
            onClick={load}
            className="text-[11px] font-bold text-bullogic-blue hover:text-bullogic-blue/80 px-2 py-1"
            title="Refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-navy">
                <th className={headClass}>Client</th>
                <th className={headClass}>Co-client</th>
                <th className={headClass}>Sequence</th>
                <th className={headClass}>Status</th>
                <th className={headClass}>Invited</th>
                <th className={`${headClass} text-right`}>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-[12px] text-[#888]">Loading...</td></tr>
              ) : error ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-[12px] text-rose">{error}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-[12px] text-[#888]">No prospects are pending in the portal. Push one from the Pipeline tab.</td></tr>
              ) : (
                rows.map(row => {
                  const meta = STATUS_META[row.status] || STATUS_META.not_started;
                  const removed = row.status === 'access_removed';
                  return (
                    <tr key={row.household_id} className={`border-b border-gray-100 ${removed ? 'opacity-70' : 'hover:bg-bone/50'}`}>
                      <td className={`${cellClass} font-bold`}>
                        {row.client_name}
                        {row.ready_to_convert && (
                          <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded-[3px] bg-deep-teal/15 text-deep-teal align-middle">Ready to convert</span>
                        )}
                      </td>
                      <td className={`${cellClass} text-[#555]`}>{row.co_client_name || '--'}</td>
                      <td className={`${cellClass} text-[#666]`}>{row.sequence}</td>
                      <td className={cellClass}>
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${meta.cls}`}>{meta.label}</span>
                      </td>
                      <td className={`${cellClass} text-[11px] text-[#888]`}>{fmtDate(row.invited_at)}</td>
                      <td className={`${cellClass} text-right`}>
                        {removed ? (
                          <span className="text-[11px] text-[#bbb]">--</span>
                        ) : (
                          <button
                            onClick={() => handleRemoveAccess(row)}
                            disabled={actioningId === row.household_id}
                            className="text-[12px] font-bold text-rose hover:text-rose/80 disabled:opacity-40"
                          >
                            {actioningId === row.household_id ? 'Removing...' : 'Remove access'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[12px] text-[#888] leading-relaxed">
        "Signed + paid" means the engagement letter is signed and payment is in, so they are ready to convert. To convert, mark the engagement on the household in BCC (create the Active engagement); it then drops off this list and becomes an active client. If a prospect never signs, use Remove access to disable both logins.
      </div>
    </div>
  );
}
